import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { scaleIn, fadeIn } from '../../utils/animations';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md'
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);
  // Close modal on escape key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  // Use a portal to render the modal at the root level of the DOM
  const modalContent = <AnimatePresence data-id="element-737">
      {isOpen && <div className="fixed inset-0 z-[9999] overflow-y-auto" data-id="element-738">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-739">
            <motion.div className="fixed inset-0 bg-black bg-opacity-50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose}
        // Ensure this div doesn't block pointer events for the modal content
        style={{
          pointerEvents: 'auto'
        }} data-id="element-740" />
            <motion.div ref={modalRef} className={`${sizeClasses[size]} relative w-full bg-white rounded-lg shadow-xl z-[10000]`} variants={scaleIn} initial="hidden" animate="visible" exit="exit"
        // Ensure this div can receive pointer events
        style={{
          pointerEvents: 'auto'
        }} data-id="element-741">
              <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-742">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-743">{title}</h3>
                <motion.button onClick={e => {
              e.stopPropagation();
              onClose();
            }} className="text-gray-400 hover:text-gray-500 focus:outline-none" whileHover={{
              scale: 1.1
            }} whileTap={{
              scale: 0.95
            }} data-id="element-744">
                  <XIcon size={20} data-id="element-745" />
                </motion.button>
              </div>
              <div className="px-6 py-4" data-id="element-746">{children}</div>
            </motion.div>
          </div>
        </div>}
    </AnimatePresence>;
  // Check if we're in a browser environment before using createPortal
  if (typeof window !== 'undefined') {
    return createPortal(modalContent, document.body);
  }
  // Fallback for SSR
  return isOpen ? modalContent : null;
};